// Auth-aware API client for the Django backend (ported from the Week 1 shell).

// Same origin by default, which is what a deployment behind one nginx always
// wants: the browser calls /api on whatever domain served the page.
//
// It used to default to http://localhost:8000/api, which is correct on a
// laptop and catastrophic anywhere else — VITE_API_BASE is read at *build*
// time, so a production build made without it shipped "call the visitor's own
// machine" to every visitor. That fails as mixed content before it even fails
// to connect, and presents as a bare "Failed to fetch" on the login screen.
//
// In development the Vite dev server proxies /api to localhost:8000 (see
// vite.config.mjs), so this same default works there too, with nothing to set.
// VITE_API_BASE still overrides, for an API on a different domain.
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

let accessToken = sessionStorage.getItem('access');
let refreshToken = sessionStorage.getItem('refresh');

function setTokens(access, refresh) {
  accessToken = access;
  refreshToken = refresh;
  if (access) sessionStorage.setItem('access', access);
  else sessionStorage.removeItem('access');
  if (refresh) sessionStorage.setItem('refresh', refresh);
  else sessionStorage.removeItem('refresh');
}

export function isLoggedIn() {
  return accessToken !== null;
}

function clearTokens() {
  setTokens(null, null);
}

// A session can die under a student who is sitting on a page working: the
// refresh token expires, or it gets rotated out from under them by a second
// tab, or an admin resets their password. Without this the app kept
// rendering normally and every request just failed, so submitting an
// exercise did nothing at all and said nothing — verified in a browser.
// AuthProvider subscribes and drops the user, which sends them to login.
let onSessionExpired = null;

export function setSessionExpiredHandler(handler) {
  onSessionExpired = handler;
}

function sessionExpired() {
  clearTokens();
  if (onSessionExpired) onSessionExpired();
}

// Blacklists the refresh token server-side (best-effort — a network hiccup
// still logs the user out locally) so a copied/stolen token from this
// session stops working immediately instead of remaining valid until it
// naturally expires.
export async function logout() {
  const tokenToRevoke = refreshToken;
  clearTokens();
  if (tokenToRevoke) {
    try {
      await fetch(`${API_BASE}/auth/logout/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: tokenToRevoke })
      });
    } catch {
      // Backend unreachable — already logged out locally, nothing more to do.
    }
  }
}

export async function login(email, password) {
  const res = await fetch(`${API_BASE}/auth/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });
  if (!res.ok) throw new Error('Invalid email or password');
  const data = await res.json();
  setTokens(data.access, data.refresh);
}

async function refreshAccess() {
  if (!refreshToken) {
    sessionExpired();
    return false;
  }
  let res;
  try {
    res = await fetch(`${API_BASE}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken })
    });
  } catch {
    // Offline or the backend is down. Not an expired session — keep the
    // tokens so the retry works once connectivity returns, rather than
    // logging out someone whose wifi dropped for a moment.
    return false;
  }
  if (!res.ok) {
    sessionExpired();
    return false;
  }
  const data = await res.json();
  setTokens(data.access, data.refresh ?? refreshToken);
  return true;
}

async function authorizedFetch(url, init) {
  const doFetch = () =>
    fetch(url, {
      ...init,
      headers: {
        ...init.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
      }
    });

  let res = await doFetch();
  if (res.status === 401 && (await refreshAccess())) {
    res = await doFetch();
  }
  return res;
}

// Wraps a write so a failure is impossible to ignore. Every caller that used
// `if (res.ok)` silently did nothing on failure — the student saw no error and
// no confirmation, which on an exercise means losing written work with no
// warning. Throws with something worth showing instead.
export async function apiWrite(path, init) {
  let res;
  try {
    res = await apiFetch(path, init);
  } catch {
    throw new Error("Couldn't reach the server. Check your connection and try again.");
  }
  if (res.ok) return res;

  if (res.status === 401) throw new Error('Your session has expired. Sign in again to save your work.');
  if (res.status === 429) throw new Error("You're doing that too quickly. Wait a moment and try again.");
  if (res.status === 403) throw new Error('You no longer have access to this lesson.');

  const body = await res.json().catch(() => ({}));
  throw new Error(body.detail || 'Something went wrong. Please try again.');
}

export async function apiFetch(path, init = {}) {
  return authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers }
  });
}

// For binary responses (slide images) — same JWT/refresh handling as
// apiFetch, but no JSON content-type forced onto what are usually GETs
// carrying a custom X-Slide-Token header instead of a body.
export async function apiFetchBlob(path, init = {}) {
  return authorizedFetch(`${API_BASE}${path}`, init);
}

// For file uploads. Deliberately sets no Content-Type: the browser has to
// supply its own multipart boundary, and overriding it breaks the upload.
export async function apiUpload(path, formData) {
  return authorizedFetch(`${API_BASE}${path}`, { method: 'POST', body: formData });
}

// Downloads an authenticated file (the CSV exports). A plain <a href> can't
// be used: these endpoints need the Authorization header, and the browser
// won't attach it to a normal navigation. So fetch it, then hand the blob to
// a synthetic link.
export async function apiDownload(path, fallbackFilename) {
  const res = await apiFetchBlob(path);
  if (!res.ok) {
    if (res.status === 401) throw new Error('Your session has expired. Sign in again to download this.');
    throw new Error('Could not generate that export. Please try again.');
  }

  // Prefer the server's filename — it carries the course name and date.
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackFilename;

  const url = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function fetchMe() {
  const res = await apiFetch('/auth/me/');
  if (!res.ok) throw new Error('Not authenticated');
  return res.json();
}
